import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import apiService from '../services/api';
import { USER_ROLES, PERMISSIONS, STORAGE_KEYS, ERROR_MESSAGES } from '../utils/constants';
import { UserCreator } from '../components';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUserCreator, setShowUserCreator] = useState(false);
  const [pendingFirebaseUid, setPendingFirebaseUid] = useState(null);

  async function login(email, password) {
    try {
      console.log('AuthContext: Attempting login with', email);
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('AuthContext: Login successful', userCredential);
      
      // Fetch user data from database
      console.log('AuthContext: Looking for user with Firebase UID:', userCredential.user.uid);
      let user = await apiService.users.getByFirebaseUid(userCredential.user.uid);
      console.log('AuthContext: User data from database by UID:', user);
      
      // If not found by Firebase UID, try by email (for existing users with mock UIDs)
      if (!user && userCredential.user.email) {
        console.log('AuthContext: No user found by UID, trying by email:', userCredential.user.email);
        user = await apiService.users.getByEmail(userCredential.user.email);
        console.log('AuthContext: User data from database by email:', user);
        
        // If found by email, update the Firebase UID in the database
        if (user) {
          console.log('AuthContext: Found user by email, updating Firebase UID in database');
          await apiService.users.updateFirebaseUid(user.id, userCredential.user.uid);
          // Update the user object with the new Firebase UID
          user.firebase_uid = userCredential.user.uid;
        }
      }
      
      if (user) {
        setUserData(user);
        setUserRole(user.role);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      } else {
        console.error('AuthContext: No user found in database with Firebase UID or email:', userCredential.user.uid, userCredential.user.email);
        // Let's also try to list all users to see what we have
        const allUsers = await apiService.users.getAll();
        console.log('AuthContext: All users in database:', allUsers);
        
        // Show the user creator dialog instead of throwing an error
        setPendingFirebaseUid(userCredential.user.uid);
        setShowUserCreator(true);
        return userCredential; // Return the credential but don't complete login
      }
      
      return userCredential;
    } catch (error) {
      console.error('AuthContext: Login failed', error);
      setError(ERROR_MESSAGES.AUTH_FAILED);
      throw error;
    }
  }

  async function loginWithPin(employeeId, pin) {
    try {
      console.log('AuthContext: Attempting PIN login with', employeeId);
      setError('');
      
      // For PIN login, we need to find the user by employee_id and verify PIN
      // Then use their email to sign in with Firebase
      const users = await apiService.users.getAll();
      const user = users.find(u => u.employee_id === employeeId && u.pin_code === pin);
      
      if (!user) {
        throw new Error('Invalid employee ID or PIN');
      }
      
      // For PIN login, we'll need to implement a custom token or use a different approach
      // For now, we'll simulate the login by setting the user data directly
      setUserData(user);
      setUserRole(user.role);
      setCurrentUser({ uid: user.firebase_uid, email: user.email, displayName: user.name });
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      
      return { user: { uid: user.firebase_uid, email: user.email, displayName: user.name } };
    } catch (error) {
      console.error('AuthContext: PIN Login failed', error);
      setError(ERROR_MESSAGES.AUTH_FAILED);
      throw error;
    }
  }

  async function loginWithToken(token) {
    try {
      console.log('AuthContext: Attempting token login');
      setError('');
      setTokenLoading(true);
      
      // For ID tokens, we need to handle this differently
      // Since we can't directly use signInWithCustomToken for ID tokens,
      // we'll decode the token to get user information and then set the auth state
      
      try {
        // Parse the JWT token to extract user information
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const decodedToken = JSON.parse(jsonPayload);
        console.log('AuthContext: Decoded token payload:', decodedToken);
        
        // Verify token is not expired
        const now = Math.floor(Date.now() / 1000);
        if (decodedToken.exp && decodedToken.exp < now) {
          throw new Error('Token has expired');
        }
        
        // Extract user information from the token
        const uid = decodedToken.user_id || decodedToken.sub;
        const email = decodedToken.email;
        const name = decodedToken.name || decodedToken.display_name;
        
        if (!uid) {
          throw new Error('Invalid token: missing user ID');
        }
        
        console.log('AuthContext: Token validated, user ID:', uid, 'email:', email);
        
        // Fetch user data from database using the UID from token
        let user = await apiService.users.getByFirebaseUid(uid);
        console.log('AuthContext: User data from database by UID:', user);
        
        // If not found by Firebase UID, try by email
        if (!user && email) {
          console.log('AuthContext: No user found by UID, trying by email:', email);
          user = await apiService.users.getByEmail(email);
          console.log('AuthContext: User data from database by email:', user);
          
          // If found by email, update the Firebase UID in the database
          if (user) {
            console.log('AuthContext: Found user by email, updating Firebase UID in database');
            await apiService.users.updateFirebaseUid(user.id, uid);
            user.firebase_uid = uid;
          }
        }
        
        if (user) {
          // Set user data in context
          setUserData(user);
          setUserRole(user.role);
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
          
          // Create a mock Firebase user object for compatibility
          const mockUser = {
            uid: uid,
            email: email,
            displayName: name,
            emailVerified: decodedToken.email_verified || false
          };
          
          setCurrentUser(mockUser);
          console.log('AuthContext: Token login successful, user data set');
          
          return { user: mockUser };
        } else {
          console.error('AuthContext: No user found in database with Firebase UID or email:', uid, email);
          
          // Show the user creator dialog for new users
          setPendingFirebaseUid(uid);
          setShowUserCreator(true);
          
          // Create mock user for the creator dialog
          const mockUser = { uid: uid, email: email, displayName: name };
          setCurrentUser(mockUser);
          return { user: mockUser };
        }
        
      } catch (tokenError) {
        console.error('AuthContext: Token parsing/validation failed:', tokenError);
        throw new Error('Invalid or malformed token: ' + tokenError.message);
      }
      
    } catch (error) {
      console.error('AuthContext: Token login failed', error);
      setError('Token authentication failed: ' + error.message);
      throw error;
    } finally {
      setTokenLoading(false);
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      setUserData(null);
      setUserRole(null);
    } catch (error) {
      console.error('AuthContext: Logout failed', error);
      throw error;
    }
  }

  async function fetchUserData(uid) {
    try {
      const user = await apiService.users.getByFirebaseUid(uid);
      if (user) {
        setUserData(user);
        setUserRole(user.role);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
        return user;
      } else {
        setUserData(null);
        setUserRole(null);
        return null;
      }
    } catch (error) {
      console.error('AuthContext: Failed to fetch user data', error);
      setUserData(null);
      setUserRole(null);
      setError('Failed to fetch user data: ' + error.message);
      return null;
    }
  }

  // Check if user has required permission
  function hasPermission(permission) {
    if (!currentUser || !userRole) return false;
    
    // Define permission sets based on roles
    const rolePermissions = {
      [USER_ROLES.DEVELOPER]: [
        'all_schools_access', 'create_schools', 'delete_schools', 
        'manage_school_admins', 'view_all_analytics', PERMISSIONS.SYSTEM_SETTINGS,
        'manage_developers', 'backup_restore', 'billing_access'
      ],
      [USER_ROLES.ADMIN]: [
        'manage_teachers', 'manage_moderators', 'manage_students', 
        'manage_guardians', 'manage_devices', 'view_analytics', 
        'modify_school_settings', 'delete_data', 'manage_classes', 
        'view_financial_reports', PERMISSIONS.EXPORT_DATA
      ],
      [USER_ROLES.MODERATOR]: [
        'view_teachers', 'view_students', 'view_guardians', 
        'add_guests', PERMISSIONS.APPROVE_GUARDIANS, 'view_pickup_logs', 
        'view_basic_analytics', 'manage_own_profile', 'moderate_content'
      ],
      [USER_ROLES.TEACHER]: [
        'view_own_students', 'sign_out_students', 'view_pickup_logs', 
        'manual_license_entry', 'manage_own_profile', 'view_class_roster'
      ]
    };
    
    return rolePermissions[userRole]?.includes(permission) || false;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('AuthContext: Auth state changed', user);
      setCurrentUser(user);
      
      if (user) {
        // Try to get user data from localStorage first
        const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        if (storedUserData) {
          const parsedUserData = JSON.parse(storedUserData);
          setUserData(parsedUserData);
          setUserRole(parsedUserData.role);
        } else {
          // Fetch from database if not in localStorage
          await fetchUserData(user.uid);
        }
      } else {
        setUserData(null);
        setUserRole(null);
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    userRole,
    login,
    loginWithPin,
    loginWithToken,
    logout,
    hasPermission,
    error,
    loading,
    tokenLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      <UserCreator 
        open={showUserCreator}
        onClose={() => {
          setShowUserCreator(false);
          setPendingFirebaseUid(null);
        }}
        firebaseUid={pendingFirebaseUid}
      />
    </AuthContext.Provider>
  );
}
